import { getBridge } from './bridge'

type EventName = string
type EventListener<T = any> = (payload: T) => void

const eventNameToListeners = new Map<EventName, Set<EventListener>>()
let isBridgeRegistered = false

function getOrCreateListenerSet(eventName: EventName): Set<EventListener> {
  let set = eventNameToListeners.get(eventName)
  if (!set) {
    set = new Set<EventListener>()
    eventNameToListeners.set(eventName, set)
  }
  return set
}

function safeJsonParse<T = any>(input: any): T | any {
  if (typeof input !== 'string') return input
  try {
    return JSON.parse(input)
  } catch {
    return input
  }
}

async function ensureBridgeRegistration(): Promise<void> {
  if (isBridgeRegistered) return
  const bridge = await getBridge()
  if (!bridge || typeof bridge.registerHandler !== 'function') {
    // 兜底方案：仅暴露全局 pageCallback，供原生直接调用 JS 事件
    ;(window as any).pageCallback = (eventName: string, payload?: any) => {
      emit(eventName, payload)
    }
    isBridgeRegistered = true
    return
  }

  // 统一的 原生 -> JS 事件入口
  bridge.registerHandler('emitEvent', (data: any, responseCallback?: (resp: any) => void) => {
    const parsed = safeJsonParse<{ event: string; payload?: any }>(data)
    if (parsed && parsed.event) {
      emit(parsed.event, parsed.payload)
    }
    if (responseCallback) responseCallback({ status: 'ok' })
  })

  // 可选：注册探活/占位，便于原生检测 JS 处理器是否存在
  bridge.registerHandler('registerJsHandler', (data: any, responseCallback?: (resp: any) => void) => {
    // 无实际逻辑，仅用于存在性校验
    if (responseCallback) responseCallback({ status: 'ok' })
  })

  // 提供全局兜底方式（当原生未使用 registerHandler 时可直接调用）
  ;(window as any).pageCallback = (eventName: string, payload?: any) => {
    emit(eventName, payload)
  }

  isBridgeRegistered = true
}

export async function on<T = any>(eventName: EventName, listener: EventListener<T>): Promise<() => void> {
  await ensureBridgeRegistration()
  const set = getOrCreateListenerSet(eventName)
  set.add(listener as EventListener)
  return () => off(eventName, listener)
}

export function off<T = any>(eventName: EventName, listener?: EventListener<T>): void {
  if (!listener) {
    eventNameToListeners.delete(eventName)
    return
  }
  const set = eventNameToListeners.get(eventName)
  if (set) {
    set.delete(listener as EventListener)
    if (set.size === 0) eventNameToListeners.delete(eventName)
  }
}

export async function once<T = any>(eventName: EventName, listener: EventListener<T>): Promise<() => void> {
  await ensureBridgeRegistration()
  const wrapped: EventListener<T> = (payload) => {
    try {
      listener(payload)
    } finally {
      off(eventName, wrapped)
    }
  }
  return on(eventName, wrapped)
}

export function emit<T = any>(eventName: EventName, payload?: T): void {
  const set = eventNameToListeners.get(eventName)
  if (!set || set.size === 0) return
  for (const listener of Array.from(set)) {
    try {
      listener(payload as T)
    } catch (error) {
      // 捕获并忽略监听器内部错误，避免影响其他监听器
      // eslint-disable-next-line no-console
      console.error('[nativeBridge.emit] listener error', error)
    }
  }
}

// 允许原生通过特定名称调用 JS 处理器（bridge.registerHandler）
// 此方法会在底层桥上注册处理器，同时将该名称接入事件总线
export async function registerJsHandler(handlerName: string): Promise<void> {
  const bridge = await getBridge()
  if (bridge && typeof bridge.registerHandler === 'function') {
    bridge.registerHandler(handlerName, (data: any, responseCallback?: (resp: any) => void) => {
      const parsed = safeJsonParse<any>(data)
      emit(handlerName, parsed)
      if (responseCallback) responseCallback({ status: 'ok' })
    })
  }
  await ensureBridgeRegistration()
}

// 便捷初始化：在大多数应用场景可直接调用
// 幂等保证：多次调用也只初始化一次
export async function initNativeEventBridge(): Promise<void> {
  await ensureBridgeRegistration()
}

// 使用示例：
// await initNativeEventBridge()
// on('appLifecycleDidEnterForeground', (payload) => { /* 业务逻辑 */ })
// once('paymentCompleted', (result) => { /* 仅触发一次 */ })

